import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { UnstyledButton } from '@any-ui-react/core';
import SnapSlider from '@tannerhodges/snap-slider';
import classNames from 'classnames';

import { DotIcon } from '@anyx/shared-icons';


const DEFAULT_START = 1;

export type PreviewSliderHandle = {
  goto: (index: number) => void;
};

export interface PreviewSliderProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  classes?: {
    slider?: string;
    slides?: string;
  };
}

export const PreviewSlider = React.forwardRef<PreviewSliderHandle, PreviewSliderProps>(
  ({ id, children, className, classes }, ref) => {
    const sliderDivRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<SnapSlider>(null);
    const [currentSlide, setCurrentSlide] = useState(DEFAULT_START);
    const [slideNavs, setSlideNavs] = useState<(string | number)[]>([]);

    const childrenLength = React.Children.count(children);

    useImperativeHandle(ref, function () {
      return {
        goto: (index: number) => {
          sliderRef.current?.goto(index, undefined, undefined);
        },
      };
    });

    useEffect(() => {
      sliderRef.current = new SnapSlider(sliderDivRef.current, {
        start: DEFAULT_START,
        id,
        nav: `.${id}-nav`,
        on: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          change: (e: any) => {
            setCurrentSlide(e.current);
          },
        },
      });

      return () => {
        sliderRef?.current?.destroy?.();
      };
    }, [id]);

    useEffect(() => {
      const start = sliderRef?.current?.current;
      sliderRef?.current?.reset?.({
        start,
      });
    }, [childrenLength]);

    useEffect(() => {
      setSlideNavs(
        Array.from({ length: childrenLength }).map((_, i) =>
          i + 1 === currentSlide ? 'current' : i + 1
        )
      );
    }, [currentSlide, childrenLength]);

    return (
      <div className={classNames('flex flex-col gap-y-2', className)}>
        <div
          className={`scrollfix snap-slider-center scrollbar-hidden ${classNames(classes?.slider)}`}
          ref={sliderDivRef}
        >
          {React.Children.map(children, (child, i) => {
            return (
              <div className={`snap-slides ${classNames(classes?.slides)}`} key={i}>
                {child}
              </div>
            );
          })}
        </div>
        {childrenLength > 1 && (
          <ul
            className={`${id}-nav snap-slider-nav grid auto-cols-max grid-flow-col justify-center gap-x-2`}
          >
            {slideNavs.map((nav) => {
              return (
                <li key={nav}>
                  <UnstyledButton data-snap-slider-goto={nav}>
                    <DotIcon
                      className={`h-2 w-2 ${
                        nav === 'current' ? 'text-gray-5' : 'text-gray-5/[0.4]'
                      }`}
                    />
                  </UnstyledButton>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }
);
